import { postResource, postSimpleResource, getResource } from './handleFetch.service';

// Per-batch owner secret. The server binds a converter batch to the key sent
// with the request that created it (upload, preview, restore) and asks for it
// again on append/optimize/render, so a batch id alone (it leaks, e.g. in MCP
// download links) no longer lets anyone touch the batch. Kept in memory only:
// the converter's own state does not survive a reload either.
const batchKeys = new Map();

const newBatchKey = () => {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
};

// `create` only where this tab creates the batch; a batch this tab did not
// create (admin views) gets no key and relies on the session token.
const batchKeyFor = (id, { create = false } = {}) => {
  if (!id) return undefined;
  if (!batchKeys.has(id)) {
    if (!create) return undefined;
    batchKeys.set(id, newBatchKey());
  }
  return batchKeys.get(id);
};

export const optimize = async (clientId, config, user, token) => postResource(
  `converter/optimize/${clientId}`,
  { ...config, user, batchKey: batchKeyFor(clientId) },
  token,
);

export const uploadFiles = async (id, files, token) => {
  const data = new FormData();
  files.forEach((file, i) => {
    data.append(`files-${i}`, file, file.name);
  });
  data.append('batchKey', batchKeyFor(id, { create: true }));
  return postSimpleResource(`converter/upload-files/${id}`, data, token);
};

export const getTotalConverted = async () => await getResource('users/stats');

export const getUserStats = async (token) => getResource('converter/get_stats', token);

export const renderSvg = async (optimizeId, mockValues, options = {}) => {
  const body = {
    mockValues,
    fileName: options.fileName || null,
    debug: options.debug || false,
    svghmiContent: options.svghmiContent || null,
    returnMetadata: options.returnMetadata || false,
    userId: options.userId || null,
    // A preview of an uploaded .svghmi creates its batch here.
    batchKey: batchKeyFor(optimizeId, { create: Boolean(options.svghmiContent) }),
  };
  return postResource(`converter/render/${optimizeId}`, body, options.token);
};

export const restoreConversion = async (fileId, token) => {
  // The restored session gets a fresh batch id from the server; bind it to a
  // key of this tab so render/optimize on it work like on an upload.
  const batchKey = newBatchKey();
  const result = await postResource(`converter/restore/${fileId}`, { batchKey }, token);
  if (result?.newId) batchKeys.set(result.newId, batchKey);
  return result;
};
